// /admin → Changelog: write the posts that appear on myumaps.ca/mobile/changelog.
//
// Its own file for the reason EventsAdmin.js is: AdminPage.js is a dashboard of
// tables, and this is the one screen there that AUTHORS something rather than
// displaying it. It has a draft, a publish step and a picker, none of which the
// rest of that file has any use for.
//
// ## The shape
//
// A list of posts on the left of the flow and a composer that opens over it:
// pick New post, or a post to edit, and everything happens in the same form.
// There is no separate "edit" screen, because there are perhaps thirty of these
// ever and a second screen would be a second thing to keep in step.
//
// ## ⚠️ Attaching feedback COPIES it
//
// The picker lists bad route ratings and problem reports side by side, newest
// first. Ticking one copies its time, device and comment into the post — it
// does not link to the row. Two consequences the operator can see on this
// screen, and both are deliberate:
//
//  · The quote is EDITABLE afterwards. This is the only place in the codebase
//    where a student's free text becomes public, and the operator has to be
//    able to trim it, fix a typo, or cut a sentence somebody typed in anger —
//    without touching the evidence in the queue it came from.
//  · Clearing the queue later does not empty the post. Reports and ratings roll
//    over on their own caps; a published entry has to outlive them.
//
// The time shown is the time the STUDENT sent it, never the time it was
// attached — "reported the 3rd, fixed the 10th" is the entire reason to quote
// it at all.

import { useCallback, useMemo, useState } from 'react';
import { fmtTime, destLabel } from '../utils/adminLabels.js';

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';

const KINDS = [
  { id: 'added', label: 'New' },
  { id: 'fixed', label: 'Fixed' },
  { id: 'improved', label: 'Better' },
  { id: 'known', label: 'Known issue' },
];

const KIND_LABEL = Object.fromEntries(KINDS.map((k) => [k.id, k.label]));

const MAX_ITEMS = 12;

const blankDraft = () => ({
  id: '', kind: 'fixed', version: '', title: '', body: '', published: false, items: [],
});

/**
 * A stable identity for one pickable piece of feedback.
 *
 * Reports have no id either (same capped-list story as the ratings), so this is
 * derived the same way — and it only has to be unique within the picker, which
 * is one render of two lists, so time plus a slice of the text is plenty.
 */
const feedKey = (f) => `${f.source}:${f.ts}:${(f.text || '').slice(0, 24)}`;

export default function ChangelogAdmin({ token, posts, reports, ratings, onChanged }) {
  const [draft, setDraft] = useState(null); // null = not composing
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  /**
   * Everything attachable, as one list.
   *
   * ⚠️ Bad ratings ONLY. An "ok" or "good" rating carries no note by design
   * (see api/_routeRatings.js), so it has nothing to quote — and a changelog
   * entry saying "students told us:" over a silent thumbs-up would be inventing
   * a sentence somebody never wrote. Reports are all included: a report is
   * prose somebody chose to write, so every one of them has something in it.
   */
  const feedback = useMemo(() => {
    const out = [];
    for (const r of reports || []) {
      if (!r?.message) continue;
      out.push({
        source: 'report',
        ts: r.ts,
        device: r.session?.device || 'Unknown device',
        text: r.message,
        where: destLabel(r.session?.to),
      });
    }
    for (const r of ratings || []) {
      if (r?.verdict !== 'bad' || !r?.note) continue;
      out.push({
        source: 'rating',
        ts: r.ts,
        device: r.device || 'Unknown device',
        text: r.note,
        where: destLabel(r.to),
      });
    }
    return out.sort((a, b) => (b.ts || 0) - (a.ts || 0));
  }, [reports, ratings]);

  const attachedKeys = useMemo(
    () => new Set((draft?.items || []).map(feedKey)),
    [draft],
  );

  const shown = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return feedback.slice(0, 80);
    return feedback
      .filter((f) => `${f.text} ${f.device} ${f.where}`.toLowerCase().includes(q))
      .slice(0, 80);
  }, [feedback, pickerQuery]);

  const save = useCallback(async (patch) => {
    setBusy('save'); setErr('');
    try {
      const r = await fetch(`${API_BASE}/api/changelog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ op: 'save', ...patch }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 401) { setErr('Only the operator account can post here.'); return null; }
      if (!r.ok || j.ok === false) { setErr(j.error || 'That did not save.'); return null; }
      await onChanged?.();
      return j.post;
    } catch { setErr('Network error.'); return null; }
    finally { setBusy(''); }
  }, [token, onChanged]);

  const remove = useCallback(async (post) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(post.published
      ? `Delete “${post.title}”? It is live on the changelog right now, and this cannot be undone.`
      : `Delete this draft? This cannot be undone.`)) return;
    setBusy(`del:${post.id}`); setErr('');
    try {
      const r = await fetch(`${API_BASE}/api/changelog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ op: 'delete', id: post.id }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.ok === false) { setErr(j.error || 'That did not go through.'); return; }
      if (draft?.id === post.id) setDraft(null);
      await onChanged?.();
    } catch { setErr('Network error.'); }
    finally { setBusy(''); }
  }, [token, onChanged, draft]);

  const live = (posts || []).filter((p) => p.published).length;

  return (
    <div>
      <div style={S.top}>
        <div>
          <div style={S.topTitle}>myumaps.ca/mobile/changelog</div>
          <div style={S.topNote}>
            {live} live, {(posts || []).length - live} draft
            {(posts || []).length - live === 1 ? '' : 's'} ·{' '}
            <a style={S.link} href="/mobile/changelog" target="_blank" rel="noreferrer">
              see the page ↗
            </a>
          </div>
        </div>
        {!draft && (
          <button style={S.btnPrimary} onClick={() => { setDraft(blankDraft()); setErr(''); }}>
            New post
          </button>
        )}
      </div>

      {!!err && <div style={S.err}>{err}</div>}

      {/* ── The composer ─────────────────────────────────────────────────── */}
      {draft && (
        <div style={S.composer}>
          <div style={S.row}>
            <div style={S.fieldGrow}>
              <label style={S.label}>Title</label>
              <input
                style={S.input}
                value={draft.title}
                maxLength={120}
                placeholder="Tunnel routes no longer send you outside"
                onChange={(e) => set({ title: e.target.value })}
              />
            </div>
            <div style={S.fieldNarrow}>
              <label style={S.label}>Version <span style={S.opt}>optional</span></label>
              <input
                style={S.input}
                value={draft.version}
                maxLength={24}
                placeholder="1.4.0"
                onChange={(e) => set({ version: e.target.value })}
              />
            </div>
          </div>

          <div style={S.field}>
            <label style={S.label}>Kind</label>
            <div style={S.chips}>
              {KINDS.map((k) => (
                <button
                  key={k.id}
                  style={{ ...S.chip, ...(draft.kind === k.id ? S.chipOn : {}) }}
                  onClick={() => set({ kind: k.id })}
                >{k.label}</button>
              ))}
            </div>
          </div>

          <div style={S.field}>
            <label style={S.label}>
              What changed <span style={S.opt}>blank line between paragraphs; no formatting</span>
            </label>
            <textarea
              style={S.textarea}
              rows={5}
              value={draft.body}
              maxLength={8000}
              placeholder="The router was preferring the outdoor path when a tunnel was open…"
              onChange={(e) => set({ body: e.target.value })}
            />
          </div>

          {/* ── Attached feedback ──────────────────────────────────────────── */}
          <div style={S.field}>
            <label style={S.label}>
              Feedback this answers <span style={S.opt}>{draft.items.length}/{MAX_ITEMS}</span>
            </label>

            {draft.items.length === 0 && !pickerOpen && (
              <div style={S.emptyNote}>
                Nothing attached. A student who reports a bad route hears nothing
                back unless they find it here.
              </div>
            )}

            {draft.items.map((it, i) => (
              <div key={i} style={S.attached}>
                <div style={S.attachedTop}>
                  <span style={S.srcTag}>{it.source === 'rating' ? 'Bad rating' : 'Report'}</span>
                  <span style={S.attachedMeta}>
                    {[fmtTime(it.ts), it.device].filter(Boolean).join(' · ')}
                  </span>
                  <button
                    style={S.remove}
                    onClick={() => set({ items: draft.items.filter((_, j) => j !== i) })}
                  >Remove</button>
                </div>
                {/* Editable on purpose — see the header. What is stored and
                    published is whatever is in this box, not what was picked. */}
                <textarea
                  style={S.quoteBox}
                  rows={2}
                  maxLength={1000}
                  value={it.text}
                  onChange={(e) => set({
                    items: draft.items.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)),
                  })}
                />
              </div>
            ))}

            {draft.items.length < MAX_ITEMS && (
              <button
                style={S.btnGhost}
                onClick={() => { setPickerOpen(!pickerOpen); setPickerQuery(''); }}
              >
                {pickerOpen ? 'Done picking' : 'Attach feedback…'}
              </button>
            )}

            {pickerOpen && (
              <div style={S.picker}>
                <input
                  style={S.input}
                  value={pickerQuery}
                  placeholder="Search what people said…"
                  onChange={(e) => setPickerQuery(e.target.value)}
                />
                <div style={S.pickList}>
                  {shown.length === 0 && <div style={S.emptyNote}>Nothing matches.</div>}
                  {shown.map((f) => {
                    const on = attachedKeys.has(feedKey(f));
                    return (
                      <button
                        key={feedKey(f)}
                        style={{ ...S.pickRow, ...(on ? S.pickRowOn : {}) }}
                        disabled={on || draft.items.length >= MAX_ITEMS}
                        onClick={() => set({
                          items: [...draft.items, {
                            source: f.source, ts: f.ts, device: f.device, text: f.text,
                          }],
                        })}
                      >
                        <span style={S.srcTag}>{f.source === 'rating' ? 'Bad rating' : 'Report'}</span>
                        <span style={S.pickText}>{f.text}</span>
                        <span style={S.pickMeta}>
                          {[fmtTime(f.ts), f.device, f.where].filter(Boolean).join(' · ')}
                        </span>
                        {on && <span style={S.pickOn}>attached</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ⚠️ Save and Publish are two buttons because they are two acts. A
              draft is a half-written thought on a dashboard; publishing puts a
              student's words on the public web. One button doing both would
              make that the accident. */}
          <div style={S.actions}>
            <button
              style={S.btnGhost}
              disabled={busy === 'save'}
              onClick={async () => { const p = await save({ ...draft, published: false }); if (p) setDraft(null); }}
            >{draft.published ? 'Unpublish and save as draft' : 'Save draft'}</button>

            <button
              style={{ ...S.btnPrimary, ...(draft.title.trim() ? {} : S.btnOff) }}
              disabled={busy === 'save' || !draft.title.trim()}
              onClick={async () => { const p = await save({ ...draft, published: true }); if (p) setDraft(null); }}
            >{busy === 'save' ? 'Saving…' : draft.published ? 'Save, keep it live' : 'Publish'}</button>

            <button style={S.btnGhost} onClick={() => { setDraft(null); setPickerOpen(false); setErr(''); }}>
              Cancel
            </button>
            {!draft.title.trim() && <span style={S.hint}>A title is needed to publish.</span>}
          </div>
        </div>
      )}

      {/* ── The posts ────────────────────────────────────────────────────── */}
      <div style={S.list}>
        {(posts || []).length === 0 && (
          <div style={S.empty}>No posts yet. The page says so rather than showing an empty list.</div>
        )}
        {(posts || []).map((p) => (
          <div key={p.id} style={S.post}>
            <div style={S.postTop}>
              <span style={{ ...S.state, ...(p.published ? S.stateLive : S.stateDraft) }}>
                {p.published ? 'Live' : 'Draft'}
              </span>
              <span style={S.kindTag}>{KIND_LABEL[p.kind] || p.kind}</span>
              {!!p.version && <span style={S.kindTag}>v{p.version}</span>}
              <span style={S.postTitle}>{p.title || <em style={S.untitled}>Untitled</em>}</span>
              <span style={S.postWhen}>{fmtTime(p.ts)}</span>
              {!!p.items?.length && (
                <span style={S.quoteCount}>
                  {p.items.length} quote{p.items.length === 1 ? '' : 's'}
                </span>
              )}
              <button
                style={S.linkBtn}
                onClick={() => {
                  setDraft({ ...blankDraft(), ...p, items: [...(p.items || [])] });
                  setPickerOpen(false); setErr('');
                }}
              >Edit</button>
              <button
                style={{ ...S.linkBtn, color: '#c5221f' }}
                disabled={busy === `del:${p.id}`}
                onClick={() => remove(p)}
              >Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const S = {
  top: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' },
  topTitle: { fontSize: 14, fontWeight: 800, color: '#202124' },
  topNote: { fontSize: 12.5, color: '#5f6368', marginTop: 2 },
  link: { color: '#1a73e8', fontWeight: 700, textDecoration: 'none' },
  err: { background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '8px 12px', fontSize: 13, fontWeight: 600, marginBottom: 10 },

  composer: { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: 16, marginBottom: 14 },
  row: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  field: { marginBottom: 12 },
  fieldGrow: { flex: 1, minWidth: 240, marginBottom: 12 },
  fieldNarrow: { width: 140, marginBottom: 12 },
  label: { display: 'block', fontSize: 11.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: '#5f6368', marginBottom: 5 },
  opt: { fontWeight: 600, letterSpacing: 0, textTransform: 'none', color: '#9aa0a6' },
  input: { width: '100%', boxSizing: 'border-box', border: '1px solid #dadce0', borderRadius: 8, padding: '8px 10px', font: 'inherit', fontSize: 14 },
  textarea: { width: '100%', boxSizing: 'border-box', border: '1px solid #dadce0', borderRadius: 8, padding: '8px 10px', font: 'inherit', fontSize: 14, lineHeight: 1.5, resize: 'vertical' },
  chips: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  chip: { background: '#fff', border: '1px solid #dadce0', borderRadius: 999, padding: '5px 12px', fontSize: 13, fontWeight: 600, color: '#3c4043', cursor: 'pointer' },
  chipOn: { background: '#1a73e8', border: '1px solid #1a73e8', color: '#fff' },

  emptyNote: { fontSize: 13, color: '#80868b', lineHeight: 1.45, margin: '0 0 8px' },
  attached: { border: '1px solid #e8eaed', borderRadius: 8, padding: 10, marginBottom: 8, background: '#fafbfc' },
  attachedTop: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' },
  attachedMeta: { fontSize: 12, color: '#5f6368' },
  srcTag: { fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', background: '#e8f0fe', color: '#1967d2', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap', flexShrink: 0 },
  remove: { marginLeft: 'auto', background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12, fontWeight: 700, color: '#c5221f', cursor: 'pointer' },
  quoteBox: { width: '100%', boxSizing: 'border-box', border: '1px solid #dadce0', borderRadius: 6, padding: '6px 8px', font: 'inherit', fontSize: 13, lineHeight: 1.45, resize: 'vertical', background: '#fff' },

  picker: { marginTop: 8, border: '1px solid #e8eaed', borderRadius: 8, padding: 10, background: '#fafbfc' },
  pickList: { maxHeight: 320, overflowY: 'auto', marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 },
  pickRow: { display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start', textAlign: 'left', width: '100%', boxSizing: 'border-box', background: '#fff', border: '1px solid #e8eaed', borderRadius: 8, padding: '8px 10px', font: 'inherit', cursor: 'pointer' },
  pickRowOn: { background: '#f1f3f4', cursor: 'default', opacity: 0.7 },
  pickText: { fontSize: 13.5, color: '#202124', lineHeight: 1.4, overflowWrap: 'anywhere' },
  pickMeta: { fontSize: 11.5, color: '#80868b' },
  pickOn: { fontSize: 11, fontWeight: 800, color: '#137333', textTransform: 'uppercase', letterSpacing: 0.4 },

  actions: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 },
  btnPrimary: { background: '#1a73e8', color: '#fff', border: '1.5px solid #1a73e8', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  btnOff: { background: '#f1f3f4', color: '#9aa0a6', border: '1.5px solid #f1f3f4', cursor: 'default' },
  btnGhost: { background: '#fff', color: '#202124', border: '1.5px solid #dadce0', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  hint: { fontSize: 12, color: '#80868b' },

  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  empty: { background: '#fff', borderRadius: 12, padding: 16, fontSize: 14, color: '#5f6368' },
  post: { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' },
  postTop: { display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', fontSize: 13, flexWrap: 'wrap' },
  state: { fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' },
  stateLive: { background: '#e6f4ea', color: '#137333' },
  stateDraft: { background: '#f1f3f4', color: '#5f6368' },
  kindTag: { background: '#f1f3f4', borderRadius: 999, padding: '3px 9px', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' },
  postTitle: { color: '#202124', fontWeight: 600, flex: 1, minWidth: 120, overflowWrap: 'anywhere' },
  untitled: { color: '#9aa0a6' },
  postWhen: { color: '#5f6368', whiteSpace: 'nowrap' },
  quoteCount: { color: '#1967d2', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' },
  linkBtn: { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 13, color: '#1a73e8', fontWeight: 700, cursor: 'pointer' },
};
